import { createRoot } from 'react-dom/client';
import { env } from 'config/env';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import App from 'App';
import 'index.css';
import reportWebVitals from 'reportWebVitals';
import AuthProvider from 'context/authContext';
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { translationResource } from 'constants/lang';
import HttpsRedirect from 'HttpsRedirect';
import { SidebarProvider } from 'context/mobileSidebarContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { clarity } from 'react-microsoft-clarity';
import Cookies from 'js-cookie';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig } from 'msalConfig';
import { isCphTenant } from 'util/tenant';
import { safeLocalStorage } from 'util/safeLocalStorage';

declare module 'i18next' {
    interface CustomTypeOptions {
        returnNull: false;
    }
}

// After a deploy, chunks referenced by an already-open tab may no longer
// exist on S3. Reload once to pick up the new index.html; the timestamp
// guard prevents a reload loop if the new build is broken too.
window.addEventListener('vite:preloadError', (event) => {
    try {
        const key = 'chunk-reload-at';
        const last = Number(sessionStorage.getItem(key) ?? 0);
        if (Date.now() - last > 10_000) {
            sessionStorage.setItem(key, String(Date.now()));
            event.preventDefault();
            window.location.reload();
        }
    } catch {
        // sessionStorage blocked (Safari private mode, cookies disabled).
        // Without it the guard can't survive a reload, so skip the auto
        // reload entirely rather than risk an unguarded reload loop.
    }
});

const defaultLng = isCphTenant
    ? 'dansk'
    : (safeLocalStorage.getItem('language') ?? 'dansk');
if (isCphTenant) {
    safeLocalStorage.setItem('language', 'dansk');
}

i18next.use(initReactI18next).init({
    resources: translationResource,
    lng: defaultLng,
    fallbackLng: 'dansk',
    interpolation: { escapeValue: false },
    returnNull: false,
    returnObjects: false,
});

if (env.environment === 'production') {
    const data = Cookies.get('CookieConsent');
    const listData = data?.split(',');
    if (listData) {
        listData.forEach((item) => {
            const [key, value] = item.split(':');
            if (key === 'statistics' && value === 'true') {
                clarity.init('t0r48qkwt5');
                clarity.consent(true);
                clarity.start();
            }
        });
    }
}

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: 0,
        },
    },
});

// Create MSAL instance outside component tree
const msalInstance = new PublicClientApplication(msalConfig);

const container = document.getElementById('root') as HTMLElement;
const root = createRoot(container);

// When MSAL uses a hidden iframe for silent auth, the iframe loads the
// redirectUri which bootstraps the full app. Detect this and only handle
// the redirect response — do NOT render the full app inside the iframe.
const isInIframe =
    window !== window.parent &&
    window.location.href.startsWith(msalConfig.auth.redirectUri);

if (isInIframe) {
    msalInstance.initialize().then(() => {
        msalInstance.handleRedirectPromise();
    });
    root.render(<div />);
} else {
    root.render(
        <QueryClientProvider client={queryClient}>
            <HttpsRedirect disabled={false}>
                <MsalProvider instance={msalInstance}>
                    <AuthProvider msalInstance={msalInstance}>
                        <SidebarProvider>
                            <App />
                        </SidebarProvider>
                    </AuthProvider>
                </MsalProvider>
            </HttpsRedirect>
            {import.meta.env.DEV && (
                <ReactQueryDevtools initialIsOpen={false} />
            )}
        </QueryClientProvider>
    );
}

reportWebVitals();
